import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from './config';
import ArboxSignup from './ArboxSignup';
import { useGoogleLogin } from '@react-oauth/google';
import BrandHeader from './BrandHeader';



function Profile({ jwt, onLogout }) {
  const [profile, setProfile] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [boxesLoading, setBoxesLoading] = useState(true);
  // Add state for showing all friends
  const [showAllFriends, setShowAllFriends] = useState(false);
  // Add Application state
  const [showAddApplication, setShowAddApplication] = useState(false);
  const [userApplications, setUserApplications] = useState([]);
  const [availableApps, setAvailableApps] = useState([]);
  const [deletingApp, setDeletingApp] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [calendarActionLoading, setCalendarActionLoading] = useState(false);
  const [settingsError, setSettingsError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(`${API_BASE}/profile`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        setProfile(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch profile');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [jwt]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setSettingsLoading(true);
        setSettingsError(null);
        const res = await axios.get(`${API_BASE}/settings`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        setSettings(res.data);
      } catch (err) {
        setSettingsError(err.response?.data?.detail || err.message || 'Failed to fetch settings');
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, [jwt]);

  const connectGoogleCalendar = useGoogleLogin({
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/calendar.events.owned',
    ux_mode: 'popup',
    prompt: 'consent',
    onSuccess: async (codeResponse) => {
      try {
        setCalendarActionLoading(true);
        setSettingsError(null);
        const res = await axios.post(
          `${API_BASE}/settings/google-calendar/exchange-code`,
          {
            code: codeResponse.code,
            redirect_uri: window.location.origin,
          },
          {
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
          }
        );
        setSettings(res.data);
      } catch (err) {
        setSettingsError(err.response?.data?.detail || err.message || 'Failed to connect Google Calendar');
      } finally {
        setCalendarActionLoading(false);
      }
    },
    onError: () => {
      setSettingsError('Google Calendar authorization failed.');
    },
  });

  useEffect(() => {
    const fetchBoxes = async () => {
      try {
        setBoxesLoading(true);
        const res = await axios.get(`${API_BASE}/boxes/all-apps`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        setBoxes(res.data.data || []);
      } catch (err) {
        setBoxes([]);
      } finally {
        setBoxesLoading(false);
      }
    };
    fetchBoxes();
  }, [jwt]);

  // Load user applications and available applications
  useEffect(() => {
    const loadApplications = async () => {
      try {
        // Load user's current applications from backend (with proper app details)
        const userAppsResponse = await axios.get(`${API_BASE}/applications`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        setUserApplications(userAppsResponse.data.applications || []);

        // Load available applications
        const response = await axios.get(`${API_BASE}/available-applications`);
        setAvailableApps(response.data.applications || []);
      } catch (err) {
        console.error('Failed to load applications:', err);
      }
    };

    loadApplications();
  }, [jwt]);

  const handleAddApplicationSuccess = (token) => {
    // Refresh the page to reload all data with new application
    window.location.reload();
  };

  const handleAddApplicationError = (error) => {
    setError(`Failed to add application: ${error}`);
    setShowAddApplication(false);
  };

  const handleDeleteApplication = async (appId) => {
    try {
      await axios.delete(`${API_BASE}/applications/${appId}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      // Refresh applications list
      const userAppsResponse = await axios.get(`${API_BASE}/applications`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      setUserApplications(userAppsResponse.data.applications || []);

      // Close confirmation dialog
      setShowDeleteConfirm(false);
      setDeletingApp(null);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete application';
      setError(`Failed to delete application: ${errorMessage}`);
      setShowDeleteConfirm(false);
      setDeletingApp(null);
    }
  };

  const confirmDeleteApplication = (app) => {
    setDeletingApp(app);
    setShowDeleteConfirm(true);
  };

  const handleCalendarToggle = async (enabled) => {
    try {
      setCalendarActionLoading(true);
      setSettingsError(null);
      const res = await axios.patch(
        `${API_BASE}/settings`,
        { calendar_events_enabled: enabled },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setSettings(res.data);
    } catch (err) {
      setSettingsError(err.response?.data?.detail || err.message || 'Failed to update settings');
    } finally {
      setCalendarActionLoading(false);
    }
  };

  const handleCalendarDisconnect = async () => {
    try {
      setCalendarActionLoading(true);
      setSettingsError(null);
      const res = await axios.post(
        `${API_BASE}/settings/google-calendar/disconnect`,
        {},
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setSettings(res.data);
    } catch (err) {
      setSettingsError(err.response?.data?.detail || err.message || 'Failed to disconnect Google Calendar');
    } finally {
      setCalendarActionLoading(false);
    }
  };

  const getUnconnectedApps = () => {
    const connectedAppIds = userApplications.map(app => app.app_id);
    return availableApps.filter(app => !connectedAppIds.includes(app.app_id));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };

  const renderProfileInfo = () => {
    if (!profile?.data) return null;
    const userData = profile.data;
    
    return (
      <div className="space-y-6">
        {/* Profile Header */}
        <div className="flex items-center space-x-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
          {userData.image && (
            <img 
              src={userData.image} 
              alt="Profile" 
              className="w-20 h-20 rounded-full border-4 border-white shadow-lg"
            />
          )}
          <div>
            <h3 className="text-2xl font-bold text-gray-800">
              {userData.full_name || `${userData.first_name} ${userData.last_name}`}
            </h3>
            <p className="text-gray-600">{userData.email}</p>
            {userData.bio && (
              <p className="text-gray-700 mt-1 italic">"{userData.bio}"</p>
            )}
          </div>
        </div>

        {/* Personal Information */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h4 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Personal Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Birthday:</span>
              <span className="text-gray-800">{formatDate(userData.birthday)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Gender:</span>
              <span className="text-gray-800 capitalize">{userData.gender || 'Not specified'}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Country:</span>
              <span className="text-gray-800">{userData.country || 'Not specified'}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">City:</span>
              <span className="text-gray-800">{userData.city || 'Not specified'}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Phone:</span>
              <span className="text-gray-800">{userData.phone || 'Not specified'}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Member Since:</span>
              <span className="text-gray-800">{formatDate(userData.created_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Last Login:</span>
              <span className="text-gray-800">{formatDate(userData.last_login)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Account Status:</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                userData.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {userData.verified ? 'Verified' : 'Pending Verification'}
              </span>
            </div>
          </div>
        </div>

        {/* Gym Memberships Summary */}
        {userData.boxes && userData.boxes.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Gym Memberships</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{userData.activeBoxes?.length || 0}</div>
                <div className="text-sm text-gray-600">Active Gyms</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-gray-600">{userData.inactiveBoxes?.length || 0}</div>
                <div className="text-sm text-gray-600">Inactive Gyms</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{userData.friend_connection?.length || 0}</div>
                <div className="text-sm text-gray-600">Friends</div>
              </div>
            </div>
          </div>
        )}

        {/* Friends Section */}
        {userData.friend_connection && userData.friend_connection.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Friends ({userData.friend_connection.length})</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(showAllFriends ? userData.friend_connection : userData.friend_connection.slice(0, 6)).map((connection, index) => {
                const friend = connection.user || connection.friend_user;
                return (
                  <div key={connection.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    {friend.image ? (
                      <img src={friend.image} alt={friend.full_name} className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 font-medium">
                        {friend.full_name_shorten?.charAt(0) || '?'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{friend.full_name}</div>
                      {friend.bio && (
                        <div className="text-xs text-gray-500 truncate">{friend.bio}</div>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Show more/less button */}
              {userData.friend_connection.length > 6 && !showAllFriends && (
                <button
                  className="flex items-center justify-center p-3 bg-blue-50 rounded-lg text-sm text-blue-600 font-medium hover:bg-blue-100 transition-colors"
                  onClick={() => setShowAllFriends(true)}
                  type="button"
                >
                  +{userData.friend_connection.length - 6} more friends
                </button>
              )}
              {userData.friend_connection.length > 6 && showAllFriends && (
                <button
                  className="flex items-center justify-center p-3 bg-gray-50 rounded-lg text-sm text-gray-600 font-medium hover:bg-gray-100 transition-colors"
                  onClick={() => setShowAllFriends(false)}
                  type="button"
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        )}


      </div>
    );
  };

  const renderLastEndedMembership = () => {
    if (!profile?.data?.lastEndedMembership) return null;

    const userData = profile.data;
    const membership = userData.lastEndedMembership;

    return (
      <div className="mt-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h4 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Last Ended Membership</h4>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-800">{membership.box?.name}</span>
                {membership.app_name && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                    {membership.app_name}
                  </span>
                )}
              </div>
              <span className="text-sm text-gray-600">Ended: {formatDate(membership.end)}</span>
            </div>
            <div className="text-sm text-gray-600">
              Plan: {membership.membership_types?.name}
            </div>
            {membership.membership_types?.price && (
              <div className="text-sm text-gray-600">
                Price: ₪{membership.membership_types.price}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderApplications = () => {
    if (userApplications.length === 0) {
      return null; // Don't show if no applications
    }

    const unconnectedApps = getUnconnectedApps();

    return (
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4 pb-2 border-b">
          <h3 className="text-xl font-bold text-gray-800">Your Applications ({userApplications.length})</h3>
          {unconnectedApps.length > 0 && (
            <button
              onClick={() => setShowAddApplication(true)}
              className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              title="Add Application"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add</span>
            </button>
          )}
        </div>

        {/* Connected Applications */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {userApplications.map((app, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center">
                {/* App Icon */}
                <div className="flex-shrink-0 mr-4">
                  {app.icon_path ? (
                    <img
                      src={app.icon_path}
                      alt={app.name}
                      className="w-12 h-12 rounded-lg object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                      <span className="text-gray-500 font-semibold text-lg">
                        {app.name?.charAt(0) || 'A'}
                      </span>
                    </div>
                  )}
                </div>

                {/* App Details */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 truncate">{app.name}</h4>
                  <p className="text-sm text-gray-600 truncate">{app.description}</p>
                  <div className="flex items-center mt-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-xs text-green-600 font-medium">Connected</span>
                  </div>
                </div>

                {/* Delete Button */}
                {userApplications.length > 1 && (
                  <button
                    onClick={() => confirmDeleteApplication(app)}
                    className="ml-3 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete Application"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>


      </div>
    );
  };

  const renderBoxes = () => {
    if (boxesLoading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p>Loading gyms...</p>
        </div>
      );
    }
    if (!boxes.length) {
      return <div className="text-gray-500 text-center py-8">No gyms found.</div>;
    }
    return (
      <div className="mt-8">
        <h3 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Your Gym Memberships</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {boxes.map(box => (
            <div key={`${box.app_id}-${box.id}`} className={`border rounded-lg p-4 ${box.active ? 'bg-white shadow-sm' : 'bg-gray-50'}`}>
              <div className="flex items-center space-x-4 mb-3">
                {box.box?.cloudinary_image && (
                  <img src={box.box.cloudinary_image} alt="Gym Logo" className="w-16 h-16 object-contain rounded" />
                )}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-blue-700 text-lg">{box.box?.name}</div>
                    <div className="flex flex-col items-end space-y-1">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        box.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {box.active ? 'Active' : 'Inactive'}
                      </span>
                      {box.app_name && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {box.app_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-gray-700 text-sm">{box.box?.address}</div>
                  {box.box?.phone && (
                    <div className="text-gray-600 text-sm">📞 {box.box.phone}</div>
                  )}
                </div>
              </div>
              {box.box?.bio && (
                <div className="text-gray-600 text-sm mb-2 italic">"{box.box.bio}"</div>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                <div>Age: {box.age || 'N/A'}</div>
                <div>Phone: {box.phone || 'N/A'}</div>
                <div>Gender: {box.gender || 'N/A'}</div>
                <div>Country: {box.country || 'N/A'}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSettings = () => (
    <div className="mt-8">
      <h3 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Settings</h3>
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h4 className="font-semibold text-gray-900">Google Calendar</h4>
            <p className="text-sm text-gray-600 mt-1">
              {settings?.google_calendar_connected
                ? 'Calendar events are created on your primary Google Calendar for successful bookings.'
                : 'Connect Google Calendar to add events after successful bookings.'}
            </p>
            {settings?.google_calendar_connected_at && (
              <p className="text-xs text-gray-500 mt-1">
                Connected {new Date(settings.google_calendar_connected_at).toLocaleDateString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {settings?.google_calendar_connected && (
              <label className="flex items-center cursor-pointer select-none">
                <span className="text-sm text-gray-700 mr-2">Add events</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={!!settings?.calendar_events_enabled}
                    disabled={settingsLoading || calendarActionLoading}
                    onChange={(event) => handleCalendarToggle(event.target.checked)}
                    className="sr-only"
                  />
                  <div className={`block w-10 h-6 rounded-full transition-colors duration-200 ${settings?.calendar_events_enabled ? 'bg-green-400' : 'bg-gray-300'}`}></div>
                  <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow transition-transform duration-200 ${settings?.calendar_events_enabled ? 'translate-x-4' : ''}`}></div>
                </div>
              </label>
            )}
            {settings?.google_calendar_connected ? (
              <button
                onClick={handleCalendarDisconnect}
                disabled={settingsLoading || calendarActionLoading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {calendarActionLoading ? 'Updating...' : 'Disconnect'}
              </button>
            ) : (
              <button
                onClick={() => connectGoogleCalendar()}
                disabled={settingsLoading || calendarActionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
              >
                {calendarActionLoading ? 'Connecting...' : 'Connect'}
              </button>
            )}
          </div>
        </div>
        {settingsError && (
          <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {settingsError}
          </div>
        )}
        {settingsLoading && (
          <div className="mt-4 text-sm text-gray-500">Loading settings...</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fbff] py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_24px_70px_rgba(31,41,55,0.10)]">
          <BrandHeader title="Your Arbox Profile" />
          
          <div className="p-8">
            {error && (
              <div className="text-red-600 mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                <strong>Error:</strong> {error}
              </div>
            )}
            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your profile...</p>
              </div>
            )}
            {!loading && !error && renderProfileInfo()}
            {renderSettings()}
            {renderApplications()}
            {renderBoxes()}



            {/* Last Ended Membership - moved to bottom */}
            {renderLastEndedMembership()}

            {/* Logout Button */}
            <div className="mt-4 pt-6 border-t border-gray-200">
              <button
                onClick={onLogout}
                className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Application Modal */}
      {showAddApplication && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-screen overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Add New Application</h2>
              <button
                onClick={() => setShowAddApplication(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="p-4">
              {(() => {
                const userName = profile?.data?.first_name || profile?.data?.name || '';

                // Debug logging
                if (process.env.NODE_ENV === 'development') {
                  console.log('🔧 ArboxSignup Debug (SECURE):', {
                    userName: userName,
                    note: 'User email now extracted from JWT on backend for security'
                  });
                }

                return (
                  <ArboxSignup
                    onSuccess={handleAddApplicationSuccess}
                    onError={handleAddApplicationError}
                    email=""
                    userName={userName}
                    selectedAppId={getUnconnectedApps()[0]?.app_id}
                    jwt={jwt}
                    googleToken={null} // Existing users don't need Google token
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Application</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone.</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to delete <strong>{deletingApp.name}</strong>?
                  You will lose access to this application and all associated data.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeletingApp(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteApplication(deletingApp.app_id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile; 
